import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, RouterEvent } from '@angular/router';
import { StateStorageService } from '@df/browser/storage/state-storage.service';
import { App } from '@df/core/app';
import { DfEventConfig, DfEventService } from '@df/core/service/df-event';
import { Meta } from '@df/core/meta/meta';
import { TimeoutService } from '@df/ng/timeout.service';
import { ScrollService } from '@df/util/service/scroll.service';

/**
 * Use linke like http://example.com#section=sectionId
 * to scroll to that section on page viee
 * @type {String}
 */
export const ANCHOR_HASH = 'section';

/**
 * DfEvent dispatched right before ScrollTopHistory resets the scroll position on navigation.
 * Set `event.stopped = true` in a `before` listener to keep the current scroll position
 * (used by checkout to stay on the active step).
 */

declare module '@df/core/service/df-event' {
    interface IEventType {
        scrollTopReset: DfEventConfig<'scrollTopReset', undefined, void>;
    }
}

if (typeof history !== 'undefined') {
    history.scrollRestoration = 'manual';
}

@Injectable({
    providedIn: 'root'
})
export class ScrollTopHistory {
    get scrollTop(): number {
        return this.stateStorageService.get('scrollTop');
    }

    set scrollTop(scrollTop: number | undefined) {
        this.stateStorageService.set('scrollTop', scrollTop);
    }

    /**
     * Max number of tries for apply function
     * @type {Number}
     */
    protected readonly MAX_TRIES = 20;

    /**
     * Delay between tries of apply function
     * @type {Number}
     */
    protected readonly TRY_DELAY = 200;

    static MAX_SCROLL_TOLERANCE = 50;

    /**
     * Timeout id for currently awaited apply call
     * @type {Number}
     */
    protected applyTimeout?: number;

    /**
     * remains true till first nav change
     * we need this mostly for universal not to scroll on app load
     */
    protected isFirstPage = 2;

    protected meta = inject(Meta);
    protected app = inject(App);
    protected dfEvent = inject(DfEventService);
    protected scrollService = inject(ScrollService);
    protected stateStorageService = inject(StateStorageService);
    protected timeoutService = inject(TimeoutService);
    protected destroyRef = inject(DestroyRef);

    appInitialize() {
        if (this.app.isPlatformBrowser) {
            this.scrollTop = 0;
            // subscribe to router events for the scroll top history sync
            this.meta.routerEventsSync.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e: RouterEvent) => this.onRouterChange(e));
        }
    }

    protected get scrollingElement(): HTMLElement {
        return this.app.client.getRootElement();
    }

    /**
     * On router changes callback
     */
    protected onRouterChange(event: RouterEvent): void {
        if (event instanceof NavigationEnd) {
            // this check is to prevent universal->js app change jump back to top
            if (!this.isFirstPage) {
                // Dispatch before resetting the scroll position. A listener may set event.stopped = true
                // (e.g. checkout keeps the user on the active step) to skip the reset performed by the action.
                void this.dfEvent.dispatch({
                    id: 'scrollTopReset',
                    data: undefined,
                    action: () => this.resetScrollTop()
                });
            } else {
                // first start may fire before service is ready
                this.isFirstPage = 0;
            }
        } else if (event instanceof NavigationStart) {
            if (this.isFirstPage > 0) {
                this.isFirstPage--;
            } else {
                this.scrollTop = this.scrollService.getScrollTop();
            }
        }
    }

    /**
     * Gets max allowed scrollTop
     * @return {[type]} [description]
     */
    protected getScrollTopMax(): number {
        const scrollTopMax = this.scrollingElement['scrollTopMax'];
        const scrollHeight = this.scrollingElement.scrollHeight || this.app.client.height;

        return Math.floor(typeof scrollTopMax === 'number' ? scrollTopMax : scrollHeight - this.app.client.height);
    }

    /**
     * Apply scroll top from model
     * This function may require few tries cause contents need to be
     * rendered and we may need to scroll few times to unreveal further contents
     */
    protected apply(scrollTop = 0, tryNumber = 0) {
        clearTimeout(this.applyTimeout);

        if (this.scrollService.getScrollTop() > scrollTop && this.getScrollTopMax()) {
            // if current scrolltop is higher then what we target then break the process - user already scrolled the screen further
            return;
        }

        // do not jump till we can
        if (this.getScrollTopMax() + ScrollTopHistory.MAX_SCROLL_TOLERANCE > scrollTop && this.scrollingElement) {
            // try to apply
            this.scrollService.setScrollTop(scrollTop);
            // if we are not yet scrolled to where we should be lets retry, but we leave 20px
            if (scrollTop < this.getScrollTopMax() + 20) {
                return this.onSuccess(); // succeeded
            }
        }

        // try again on failure
        if (tryNumber <= this.MAX_TRIES) {
            this.applyAsync(scrollTop, tryNumber + 1);
        }
    }

    protected applyAsync(scrollTop = 0, tryNumber = 0, delay: number = this.TRY_DELAY) {
        clearTimeout(this.applyTimeout);

        this.applyTimeout = this.timeoutService.setTimeout(() => this.apply(scrollTop, tryNumber), delay);
    }

    /**
     * Resets the scroll position to the top of the page. Runs as the SCROLL_TOP_RESET_EVENT action,
     * so it is skipped when a listener stops the event.
     */
    protected resetScrollTop(): void {
        // Without this position reset you may be at the bottom of the LP when comming back.
        // Since you are not reset to be back on top, the browser keeps you stick to the bottom when a content appears.
        // Ultimately when we can finally scroll you to desired position you are already at the bottom what is MORE then
        // we need, so we do nothing (assuming that you scrolled there yourself already).
        this.timeoutService.setTimeout(() => {
            this.scrollingElement.scrollTo({ top: 0 });

            // sometimes first one fails, this is dirty fix but it seems to be working
            this.timeoutService.setTimeout(() => {
                this.scrollingElement.scrollTo({ top: 0 });
            }, 100);
        }, 0);
        this.timeoutService.setTimeout(() => this.applyAsync(this.scrollTop, 0), 20);
    }

    protected onSuccess() {}
}
